import { Autocomplete, FormControlLabel, Grid, Switch, TextField } from '@mui/material'
import { CompanyType, VesselDto, VesselType } from '@planning/app/api'
import { VesselServiceAutocomplete } from '@planning/components/autocompletes/vessel-service-autocomplete/VesselServiceAutocomplete'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { VesselItem } from '@planning/rt-stores/vessel/VesselItem'
import { TFnType, useTranslate } from '@tolgee/react'
import { CustomInputAdornment, TomAutoComplete } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useUniquenessValidation } from '../../../../hooks/useIsPropertyUnique'
import { countryCodeOptions } from '../../helpers/countryCodeOptions'

const getDefaultValues = (): VesselFormData => ({
  name: '',
  shortName: '',
  length: 0,
  width: 0,
  vesselFlag: '',
  callSign: '',
  capacity: null,
  grossTonnage: null,
  imoNumber: '',
  isActive: true,
  maxDraft: null,
  lbp: null,
  mmsi: '',
  remarks: '',
  serviceId: null,
  shippingAgentId: null,
  shippingLine: '',
  vesselType: VesselType.Container,
})

export interface VesselFormData {
  id?: number
  name: string
  shortName: string
  shippingLine?: string | null
  imoNumber?: string | null
  mmsi?: string | null
  callSign: string
  length: number
  width: number
  vesselFlag: string
  grossTonnage?: number | null
  capacity?: number | null
  maxDraft?: number | null
  shippingAgentId?: number | null
  serviceId?: number | null
  isActive?: boolean
  remarks?: string | null
  lbp?: number | null
  vesselType: VesselType
}

export const getVesselTypeLabel = (vesselType: VesselType, t: TFnType): string => {
  switch (vesselType) {
    case VesselType.Container:
      return t('container', 'Container')
    case VesselType.BargeCarrier:
      return t('bargeCarrier', 'Barge carrier')
    case VesselType.ContainerRoRo:
      return t('containerRoRo', 'Container RoRo')
    case VesselType.Feeder:
      return t('feeder', 'Feeder')
    case VesselType.GeneralCargo:
      return t('generalCargo', 'General cargo')
    case VesselType.LoLo:
      return t('loLo', 'LoLo')
    case VesselType.RoRo:
      return t('roRo', 'RoRo')
    case VesselType.SemiContainer:
      return t('semiContainer', 'Semi container')
    default:
      return vesselType
  }
}

interface Props {
  formId?: string
  vessel?: VesselDto
  vesselItems: VesselItem[]
  onSubmit: (data: VesselFormData) => Promise<void>
}
export const VesselForm = observer(({ formId, vessel, vesselItems, onSubmit }: Props) => {
  const { t } = useTranslate()

  const [isNameUnique, validateNameUniqueness] = useUniquenessValidation({
    id: vessel?.id,
    items: vesselItems.map(x => ({ id: x.id, name: x.data.name })),
  })
  const [isShortNameUnique, validateShortNameUniqueness] = useUniquenessValidation({
    id: vessel?.id,
    items: vesselItems.map(x => ({ id: x.id, name: x.data.shortName ?? '' })),
  })

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<VesselFormData>({
    defaultValues: getDefaultValues(),
  })

  useEffect(() => {
    if (vessel) {
      reset({
        ...vessel,
      })
    } else {
      reset(getDefaultValues())
    }
  }, [vessel, reset])

  const getHelperText = (field: keyof VesselFormData) => {
    if (field === 'name' && !isNameUnique)
      return t('fieldAlreadyExists', '{field} already exists.', { field: t('name', 'Name') })
    if (field === 'shortName' && !isShortNameUnique)
      return t('fieldAlreadyExists', '{field} already exists.', {
        field: t('shortName', 'Short name'),
      })

    if (errors[field]?.message) return errors[field]?.message?.toString()

    return errors[field] ? t('fieldIsRequired', 'Field is required.') : ''
  }

  const vesselTypeOptions = Object.values(VesselType).map(type => ({
    id: type,
    label: getVesselTypeLabel(type, t),
  }))

  return (
    <Grid
      container
      spacing={1.5}
      paddingTop={0.2}
      id={formId ?? 'vessel-form'}
      component='form'
      noValidate
      autoComplete='off'
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid size={12}>
        <Controller
          name='name'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('name', 'Name')}
              required
              fullWidth
              onChange={event => {
                field.onChange(event)
                validateNameUniqueness(event.target.value)
              }}
              error={!!errors.name || !isNameUnique}
              helperText={getHelperText('name')}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          name='shortName'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('shortName', 'Short Name')}
              fullWidth
              required
              onChange={event => {
                field.onChange(event)
                validateShortNameUniqueness(event.target.value)
              }}
              error={!!errors.shortName || !isShortNameUnique}
              helperText={getHelperText('shortName')}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name='shippingLine'
          rules={{ required: true }}
          render={({ field }) => (
            <CompanyAutocomplete
              label={t('vesselOperator', 'Vessel operator')}
              required
              selectedCompanyShortName={field.value}
              handleOnChange={operator => {
                field.onChange(operator?.shortName)
              }}
              filterByTypes={[CompanyType.VesselOperator]}
              error={!!errors.shippingLine}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          name='vesselType'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              options={vesselTypeOptions}
              getOptionLabel={option => option.label}
              value={vesselTypeOptions.find(opt => opt.id === field.value) ?? null}
              onChange={(_, newValue) => {
                field.onChange(newValue?.id)
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('vesselType', 'Vessel Type')}
                  required
                  error={!!errors.vesselType}
                  helperText={getHelperText('vesselType')}
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          control={control}
          name='shippingAgentId'
          render={({ field }) => (
            <CompanyAutocomplete
              label={t('shippingAgent', 'Shipping Agent')}
              selectedCompanyId={field.value}
              handleOnChange={operator => {
                field.onChange(operator?.id)
              }}
              filterByTypes={[CompanyType.ShippingAgent]}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          name='serviceId'
          control={control}
          render={({ field }) => (
            <VesselServiceAutocomplete
              label={t('defaultService', 'Default service')}
              value={field.value}
              onChange={id => field.onChange(id)}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          control={control}
          name='imoNumber'
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('imoNumber', 'IMO Number')}
              fullWidth
              required
              error={!!errors.imoNumber}
              helperText={getHelperText('imoNumber')}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          control={control}
          name='vesselFlag'
          rules={{
            required: t('fieldIsRequired', 'Field is required.'),
            minLength: {
              value: 2,
              message: t('vesselFlagLengthMustBeTwo', 'Vessel flag length must be 2.'),
            },
          }}
          render={({ field }) => (
            <TomAutoComplete
              options={countryCodeOptions}
              error={!!errors.vesselFlag}
              helperText={getHelperText('vesselFlag')}
              defaultValue={field.value}
              onChangeEntity={value => field.onChange(value)}
              label={t('vesselFlag', 'Vessel flag')}
              required
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          name='callSign'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('callSign', 'Call Sign')}
              required
              variant='outlined'
              fullWidth
              error={!!errors.callSign}
              helperText={getHelperText('callSign')}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          name='mmsi'
          control={control}
          render={({ field }) => (
            <TextField {...field} label={t('mmsi', 'MMSI')} variant='outlined' fullWidth />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          name='grossTonnage'
          control={control}
          rules={{ min: 1 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('grossTonnage', 'Gross tonnage')}
              fullWidth
              type='number'
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6,
        }}
      >
        <Controller
          name='capacity'
          control={control}
          rules={{ min: 1 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('capacity', 'Capacity')}
              fullWidth
              type='number'
              InputProps={{
                endAdornment: (
                  <CustomInputAdornment position='end' color='secondary'>
                    TEU
                  </CustomInputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Controller
          name='length'
          control={control}
          rules={{ required: true, min: 1 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('length', 'Length')}
              fullWidth
              required
              type='number'
              InputProps={{
                endAdornment: (
                  <CustomInputAdornment position='end' color='secondary'>
                    m
                  </CustomInputAdornment>
                ),
              }}
              inputProps={{
                min: 1,
              }}
              error={!!errors.length}
              helperText={getHelperText('length')}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Controller
          name='width'
          control={control}
          rules={{ required: true, min: 1 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('width', 'Width')}
              fullWidth
              required
              type='number'
              InputProps={{
                endAdornment: (
                  <CustomInputAdornment position='end' color='secondary'>
                    m
                  </CustomInputAdornment>
                ),
              }}
              inputProps={{
                min: 1,
              }}
              error={!!errors.width}
              helperText={getHelperText('width')}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Controller
          name='lbp'
          control={control}
          rules={{ min: 0.1 }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('lbp', 'LBP')}
              fullWidth
              type='number'
              InputProps={{
                endAdornment: (
                  <CustomInputAdornment position='end' color='secondary'>
                    m
                  </CustomInputAdornment>
                ),
              }}
              inputProps={{
                min: 0.1,
                step: 0.1,
                pattern: '^\\d+(\\.\\d{0,1})?$',
              }}
              onChange={e => {
                const value = e.target.value
                if (/^\d*(\.\d{0,1})?$/.test(value) || value === '') {
                  field.onChange(e)
                }
              }}
              error={!!errors.lbp}
              helperText={getHelperText('lbp')}
            />
          )}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Controller
          name='maxDraft'
          rules={{ min: 1 }}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label={t('maxDraft', 'Max Draft')}
              fullWidth
              type='number'
              InputProps={{
                endAdornment: (
                  <CustomInputAdornment position='end' color='secondary'>
                    m
                  </CustomInputAdornment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          name='remarks'
          control={control}
          render={({ field }) => (
            <TextField {...field} label={t('remarks', 'Remarks')} multiline rows={3} fullWidth />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          name='isActive'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={e => field.onChange(e.target.checked)} />
              }
              label={t('active', 'Active')}
            />
          )}
        />
      </Grid>
    </Grid>
  )
})
